import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { JournalId } from 'types';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { commonTexts, journalTexts } from 'consts/text';
import { queryClient } from 'services/reactQuery/queryClient';
import { useCurrentInstitution } from 'jotaiAtoms/currentInstitution';
import { DateRangeFilterType } from 'hooks/useDateRangeFilterType/useDateRangeFilterType';

type SpecialJournalTopicsPageNetworkManageProps = {
    journalId: JournalId;
    date: string;
    dateRangeFilterType: DateRangeFilterType;
    onTopicRemoveRequestFinish: () => void;
};

export const useSpecialJournalTopicsPageNetworkManage = ({
    journalId,
    date,
    dateRangeFilterType,
    onTopicRemoveRequestFinish,
}: SpecialJournalTopicsPageNetworkManageProps) => {
    const { id: institutionId } = useCurrentInstitution();

    const { displaySuccess, displayError } = useSimpleNotification();

    const {
        data: specialTopicsData,
        isInitialLoading: isSpecialTopicsDataInitialLoading,
        isRefetching: isSpecialTopicsDataRefetching,
        error: specialTopicsDataError,
    } = useAppQuery(
        'SPECIAL_TOPICS',
        [journalId, { date, dateRangeFilterType }],
        () =>
            dateRangeFilterType === 'WEEKLY'
                ? StaffHTTPService.specialTopics.getSpecialTopicsByWeek(journalId, date)
                : StaffHTTPService.specialTopics.getSpecialTopics(journalId, date),
        { retry: false },
    );

    const { mutate: removeSpecialTopic, isLoading: isRemoveSpecialTopicLoading } = useAppMutation(
        StaffHTTPService.specialTopics.removeSpecialTopic,
        {
            key: ['REMOVE_SPECIAL_TOPIC'],
            onSuccess: async (_, variables) => {
                onTopicRemoveRequestFinish();
                await queryClient.invalidateQueries([
                    institutionId,
                    'SPECIAL_TOPIC',
                    variables.journalId,
                    variables.specialTopicId,
                ]);
                displaySuccess(
                    journalTexts.journalDetails.groupJournal.topics.topicRemoveSuccessNotification,
                );
            },
            onError: () => {
                onTopicRemoveRequestFinish();
                displayError(commonTexts.errorMessages.actionExecution);
            },
            invalidateQueryKeys: [
                ['SPECIAL_TOPICS', journalId],
                ['LESSONS_WITHOUT_TOPIC', journalId],
            ],
        },
    );

    return {
        specialTopicsData,
        isSpecialTopicsDataInitialLoading,
        isSpecialTopicsDataRefetching,
        isRemoveSpecialTopicLoading,
        specialTopicsDataError,
        removeSpecialTopic,
    };
};
